// ============================================================
// caseUi.tsx
// Shared primitives for the redesigned case page (Stage 1 shell —
// approved mockup "CA132B" / casevo-case-page-overview.html):
//   • CASE_CARD — the standard surface card (same recipe as SETTINGS_CARD /
//     ClientInfoSections CARD: 14px radius, hairline, two-layer shadow)
//   • CIcon — inline Feather icon set (design-language §6)
//   • CaseStatusMark — the nav / setup-chain status mark (✓ ◐ ○ 🔒 !)
//   • date helpers (civil dates, UTC-formatted — same rule as formatDateUK)
// ============================================================
import React from 'react'
import type { SectionStatus } from '../../utils/sectionFlow'
import { londonToday } from '../../utils/civilDate'

export { SETTINGS_CARD as CASE_CARD } from '../settings/settingsUi'

// ── icons ────────────────────────────────────────────────────
const PATHS = {
  brief: <><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></>,
  user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  dl: <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>,
  dots: <><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" /></>,
  car: <><path d="M5 17h14M5 17a2 2 0 1 0 4 0M15 17a2 2 0 1 0 4 0" /><path d="M3 17V11l2-5h14l2 5v6" /></>,
  scale: <path d="M12 3v18M5 7h14M5 7l-3 7a4 4 0 0 0 6 0zM19 7l-3 7a4 4 0 0 0 6 0z" />,
  heart: <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />,
  flag: <><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" /></>,
  check: <polyline points="20 6 9 17 4 12" />,
  lock: <><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></>,
  arrow: <><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></>,
  right: <polyline points="9 18 15 12 9 6" />,
  plus: <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>,
  task: <><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>,
  file: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></>,
  eye: <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>,
  link: <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />,
  cal: <><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
  hist: <><path d="M3 3v5h5" /><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" /><polyline points="12 7 12 12 15 14" /></>,
  sliders: <><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></>,
  chev: <polyline points="6 9 12 15 18 9" />,
} as const
export type CIconName = keyof typeof PATHS

export const CIcon: React.FC<{ name: CIconName; size?: number; className?: string; strokeWidth?: number }> = ({
  name, size = 16, className = '', strokeWidth = 2,
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={`shrink-0 ${className}`}>
    {PATHS[name]}
  </svg>
)

// ── status mark (mockup .st) ─────────────────────────────────
// done ✓ (brand fill) · part ◐ (half fill) · empty ○ · lock · warn ! ·
// neutral → nothing. `size` 16 (nav) / 14 (setup chain) / 13 (pills).
const STATUS_LABEL: Record<SectionStatus, string> = {
  complete: 'Complete', in_progress: 'In progress', empty: 'Not started',
  locked: 'Locked', attention: 'Needs attention', neutral: '',
}
export const CaseStatusMark: React.FC<{ status: SectionStatus; size?: number }> = ({ status, size = 16 }) => {
  if (status === 'neutral') return null
  const box = { width: size, height: size }
  const label = STATUS_LABEL[status]
  if (status === 'complete') {
    return (
      <span role="img" aria-label={label} title={label} style={box}
        className="shrink-0 rounded-full grid place-items-center bg-casevo-brand text-white dark:bg-[#52a87f] dark:text-[#10251a]">
        <CIcon name="check" size={Math.round(size * 0.625)} strokeWidth={3} />
      </span>
    )
  }
  if (status === 'in_progress') {
    return (
      <span role="img" aria-label={label} title={label} style={box}
        className="shrink-0 rounded-full border-2 border-casevo-brand bg-[linear-gradient(90deg,var(--casevo-brand)_50%,transparent_50%)] dark:border-[#52a87f] dark:bg-[linear-gradient(90deg,#52a87f_50%,transparent_50%)]" />
    )
  }
  if (status === 'empty') {
    return <span role="img" aria-label={label} title={label} style={box} className="shrink-0 rounded-full border-2 border-casevo-border" />
  }
  if (status === 'locked') {
    return (
      <span role="img" aria-label={label} title={label} style={box} className="shrink-0 grid place-items-center text-casevo-text-soft">
        <CIcon name="lock" size={Math.round(size * 0.8125)} />
      </span>
    )
  }
  return (
    <span role="img" aria-label={label} title={label} style={box}
      className="shrink-0 rounded-full grid place-items-center text-[10px] font-extrabold bg-[#fdf3dc] text-[#8a6212] dark:bg-[rgba(214,170,80,0.14)] dark:text-[#dcbb72]">
      !
    </span>
  )
}

// ── dates (civil — same UTC rule as formatDateUK) ───────────
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export const ymdOf = (iso: string | null | undefined): string | null => {
  if (!iso) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null
}
// "12 Sep 2026"
export const fmtDay = (iso: string | null | undefined): string => {
  const ymd = ymdOf(iso)
  if (!ymd) return '—'
  const [y, mo, d] = ymd.split('-').map(Number)
  return `${d} ${MON[mo - 1]} ${y}`
}
// Short month + 2-digit day for the date tile.
export const tileParts = (iso: string): { mon: string; day: string } => {
  const ymd = ymdOf(iso) ?? '1970-01-01'
  const [, mo, d] = ymd.split('-').map(Number)
  return { mon: MON[mo - 1], day: String(d).padStart(2, '0') }
}
// "Today" as a civil day — the LONDON day (audit fix 1: not the browser's
// time zone, so "This date has passed" and day counts agree with the server).
export const todayYmd = (): string => londonToday()
// Whole civil days from a → b (b − a).
export const daysBetween = (a: string, b: string): number => {
  const [ay, am, ad] = a.split('-').map(Number)
  const [by, bm, bd] = b.split('-').map(Number)
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000)
}
// "in 9 days" / "today" / "4 days overdue"
export const relDays = (days: number): string =>
  days === 0 ? 'today' : days === 1 ? 'tomorrow' : days > 1 ? `in ${days} days` : days === -1 ? '1 day overdue' : `${-days} days overdue`
// "2y 11m left" (whole months between two civil days)
export const yearsMonthsLeft = (from: string, to: string): string => {
  const [fy, fm, fd] = from.split('-').map(Number)
  const [ty, tm, td] = to.split('-').map(Number)
  let months = (ty - fy) * 12 + (tm - fm)
  if (td < fd) months -= 1
  if (months < 0) return 'passed'
  // ★ (Audit) Under 2 months, whole months hid the urgency: 20 days out, 1 day
  // out and the expiry day itself all read "0m left". Count DAYS instead.
  if (months < 2) {
    const days = daysBetween(from, to)
    if (days < 0) return 'passed'
    if (days === 0) return 'expires today'
    return `${days} day${days === 1 ? '' : 's'} left`
  }
  const y = Math.floor(months / 12)
  const m = months % 12
  return `${y ? `${y}y ` : ''}${m}m left`
}
